import { F } from '@angular/cdk/keycodes';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { CompaniesService } from 'src/app/services/companies.service';
import { DonationsService } from 'src/app/services/donations.service';
import { ImportChannel } from 'src/app/services/import-channels/import-channel.model';
import { ImportChannelsService } from 'src/app/services/import-channels/import-channels.service';
import { PermissionService } from 'src/app/services/permission.service';
import { DialogImportDonationsComponent } from '../dialog-import-donations/dialog-import-donations.component';

export enum ActionCardType {
    ASSIGN = 'assign',
    PRINT_SINGLE = 'print_single',
    PRINT_PERMANENT = 'permanent',
    IMPORT_DONATIONS = 'import_donations',
}

@Component({
    selector: 'app-action-card',
    templateUrl: './action-card.component.html',
    styleUrls: ['./action-card.component.scss'],
})
export class ActionCardComponent implements OnInit, OnDestroy {
    @Input() title = '';
    @Input() caption = '';
    @Input() type!: ActionCardType;

    @Output() getData = new EventEmitter<void>();

    amount = -1;
    private readonly amountSubject = new Subject<number>();
    $amountObservable = this.amountSubject.asObservable();

    channels: ImportChannel[] = [];

    donationPrintDue = false;

    actionCardType = ActionCardType;

    private readonly destroy = new Subject<void>();

    constructor(
        private readonly donationsService: DonationsService,
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly channelsService: ImportChannelsService,
        private readonly permission: PermissionService,
        private readonly companiesService: CompaniesService,
    ) {}

    ngOnInit(): void {
        this.setAmounts();

        this.donationsService.donationDeletedSub.pipe(takeUntil(this.destroy)).subscribe(() => {
            this.setAmounts();
        });
    }

    ngOnDestroy(): void {
        this.destroy.next();
        this.destroy.complete();
    }

    get canSendReceipts(): boolean {
        return this.permission.RECEIPTS;
    }

    get canImportChannels(): boolean {
        return this.permission.CHANNEL_IMPORT;
    }

    setAmounts(): void {
        switch (this.type) {
            case ActionCardType.ASSIGN:
                this.getDonationsToAssign();
                break;
            case ActionCardType.PRINT_SINGLE:
                this.getSingleDonationsToPrint();
                break;
            case ActionCardType.PRINT_PERMANENT:
                this.getPermanentDonationsToPrint();
                break;
            case ActionCardType.IMPORT_DONATIONS:
                this.getChannels();
                break;

            default:
                break;
        }
    }

    getDonationsToAssign(): void {
        this.donationsService
            .count({ withoutDonator: true, checkAnonymous: true, archived: false })
            .subscribe((res: number) => {
                this.amountSubject.next(res);
                this.amount = res;
                this.donationsService.setFinishedTasks({ type: 'assign', value: this.amount === 0 });
            });
    }

    getSingleDonationsToPrint(): void {
        this.donationsService
            .count({
                withoutDonator: false,
                checkAnonymous: true,
                permanentDonation: false,
                hasReceipt: false,
                archived: false,
                paid: true,
            })
            .subscribe((res: number) => {
                this.amountSubject.next(res);
                this.amount = res;
                this.donationsService.setFinishedTasks({ type: 'print-single', value: this.amount === 0 });
            });
    }

    getPermanentDonationsToPrint(): void {
        forkJoin([
            this.donationsService.count({
                withoutDonator: false,
                permanentDonation: true,
                hasReceipt: false,
                archived: false,
                checkAnonymous: true,
                paid: true,
            }),
            this.companiesService.findMine(),
        ]).subscribe((res) => {
            this.amountSubject.next(res[0]);
            this.amount = res[0];
            this.donationsService.setFinishedTasks({ type: 'print-permanent', value: this.amount === 0 });

            const printDates = res[1].settings?.printDates.length
                ? res[1].settings.printDates
                : [dayjs('01.01.2023', 'DD.MM.YYYY')];

            for (const date of printDates) {
                if (dayjs().date() === dayjs(date).date() && dayjs().month() === dayjs(date).month()) {
                    this.donationPrintDue = true;
                    break;
                }
            }
        });
    }

    getChannels(openDialog = false): void {
        this.channelsService.find().subscribe((res: ImportChannel[]) => {
            this.channels = res.sort((a, b) => ((a.latestImport ?? 0) > (b.latestImport ?? 0) ? -1 : 1));

            const latest = this.channels.find((c) => c.latestImport !== undefined);
            this.caption = `Zuletzt: ${latest ? dayjs(latest.latestImport).format('DD.MM.YYYY HH:mm') : 'nie'}`;

            if ((latest && dayjs().diff(dayjs(latest.latestImport), 'hour') < 24) || this.channels.length === 0) {
                this.amount = 0;
                this.amountSubject.next(0);
            } else {
                this.amountSubject.next(-1);
            }

            this.donationsService.setFinishedTasks({ type: 'import', value: this.amount === 0 });

            if (openDialog) {
                this.dialog
                    .open(DialogImportDonationsComponent, { width: '800px', data: { channels: this.channels } })
                    .afterClosed()
                    .subscribe((res) => {
                        if (res) {
                            this.getData.emit();
                            this.getChannels();
                        }
                    });
            }
        });
    }

    navigate(): void {
        if (this.amount !== 0 || this.type === ActionCardType.IMPORT_DONATIONS) {
            switch (this.type) {
                case ActionCardType.ASSIGN:
                    this.router.navigate(['/manual-assign'], { queryParams: { isLoop: true } });
                    break;
                case ActionCardType.PRINT_SINGLE:
                    if (this.canSendReceipts) {
                        this.router.navigate(['/sammeldruck/einzelspenden']);
                    }
                    break;
                case ActionCardType.PRINT_PERMANENT:
                    if (this.canSendReceipts) {
                        this.router.navigate(['/sammeldruck/dauerspenden']);
                    }
                    break;
                case ActionCardType.IMPORT_DONATIONS:
                    if (this.canImportChannels) {
                        this.getChannels(true);
                    }
                    break;

                default:
                    break;
            }
        }
    }
}
